import React, {Component} from "react";
import {Control, Field, Icon, Input} from "bloomer";
import Card from "../../../elements/Card";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import InformationCard from "../../../elements/InformationCard";
import Spacer from "../../../elements/Spacer";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import {getAirportCodes, getTripPurpose} from "../../../api/models/TripPurpose";
import SectionName from "../../../elements/SectionName";
import InputWarning from "../../../elements/InputWarning";
import styled from "react-emotion";
import Button from "../../../elements/Button";
import FieldDescription from "../../../elements/FieldDescription";

const LocationContainer = styled("p")`
  margin-left: 15px;
`;

const SmallSection = styled("h4")`
  margin: 0px 0px 5px 0px;
  font-size: 14px;
  font-weight: 700;
`;

const SearchResult = styled("div")`
  border-bottom: 1px solid lightgrey;
  display: flex;
  padding: 1px 5px;
  
  :hover {
    cursor: pointer;
    color: white !important;
    background: #2f3241 !important;
  }
`;

const SearchResultContainer = styled("p")`
  overflow-y: auto;
  max-height: 250px;
`;


interface ITripPurposeModelState {
    loading: boolean;
    departureDate: any;
    returnDate: any;
    departureDateInvalid: boolean;
    returnDateInvalid: boolean;
    loadingOriginLocations: boolean;
    loadingDestinationLocations: boolean;
    showOriginSearchResult: boolean;
    showDestinationSearchResult: boolean;
    originSearchText: string;
    destinationSearchText: string;
    origins: any;
    destinations: any;
    origin: any;
    destination: any;
    originInvalid: boolean;
    destinationInvalid: boolean;
    result: any;
    errorMessage: string;
}

class TripPurposeModel extends Component<{}, ITripPurposeModelState> {
    public DAY = 86400000;
    public locationFieldStyle = {
        fontSize: '12px',
        color: 'grey'
    };
    public timeout: any = 0;
    public dateInputStyle = {
        padding: '6px',
        borderRadius: '3px',
        border: '1px solid lightgrey',
        color: '#4a4a4a'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            departureDate: this.formatDate(this.truncateDate(new Date(new Date().getTime() + this.DAY))),
            departureDateInvalid: false,
            returnDate: this.formatDate(this.truncateDate(new Date(new Date().getTime() + 2 * this.DAY))),
            returnDateInvalid: false,
            loadingOriginLocations: false,
            loadingDestinationLocations: false,
            showOriginSearchResult: false,
            showDestinationSearchResult: false,
            originSearchText: "",
            destinationSearchText: "",
            origins: [],
            destinations: [],
            origin: null,
            destination: null,
            originInvalid: false,
            destinationInvalid: false,
            result: null,
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.TRIP_PURPOSE);
    }

    public componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    public truncateDate = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };

    public formatDate = (date: any) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return [
            date.getFullYear(),
            month > 9 ? month : ("0" + month),
            day > 9 ? day : ("0" + day)
        ].join('-');
    };

    public locationMapper = (item: any) => {
        return {
            name: item[0],
            code: item[1]
        }
    };

    public handleDepartureDateChange = (e: any) => {
        const date = e.target.value;
        let valid = true;
        if (date) {
            if (new Date(date) < this.truncateDate(new Date(new Date().getTime() + this.DAY))) {
                valid = false;
            }
            if (this.state.returnDate && new Date(date) > new Date(this.state.returnDate)) {
                valid = false;
            }
        }
        this.setState({
            departureDate: date,
            departureDateInvalid: !valid
        });
    }

    public handleReturnDateChange = (e: any) => {
        const date = e.target.value;
        let valid = true;
        if (date) {
            if (new Date(date) < this.truncateDate(new Date(new Date().getTime() + this.DAY))) {
                valid = false;
            }
            if (this.state.departureDate && new Date(this.state.departureDate) > new Date(date)) {
                valid = false;
            }
        }
        this.setState({
            returnDate: date,
            returnDateInvalid: !valid
        });
    }

    public handleOriginFieldFocus = () => {
        if (this.state.origins) {
            return;
        }

        this.setState({
            showOriginSearchResult: true,
            originSearchText: this.state.origin ? this.state.origin.name : ""
        });
    };

    public handleDestinationFieldFocus = () => {
        if (this.state.destinations) {
            return;
        }

        this.setState({
            showDestinationSearchResult: true,
            destinationSearchText: this.state.destination ? this.state.destination.name : ""
        });
    };

    public handleOriginSearchTextChange = async (e: any) => {
        clearTimeout(this.timeout);
        const searchText = e.currentTarget.value;
        this.setState({
            originSearchText: searchText,
            showOriginSearchResult: true,
            originInvalid: false
        }, async () => {
            if (!searchText.trim()) {
                this.setState({
                    origin: null,
                    origins: []
                });
            } else {
                this.timeout = setTimeout(async () => {
                    this.setState({loadingOriginLocations: true});
                    const response = await getAirportCodes(searchText.trim());
                    this.setState({loadingOriginLocations: false});
                    if (response && response.data) {
                        this.setState({
                            origins: response.data.map(this.locationMapper),
                            showOriginSearchResult: true
                        });
                    }
                }, 500);
            }
        });
    };

    public handleDestinationSearchTextChange = async (e: any) => {
        clearTimeout(this.timeout);
        const searchText = e.currentTarget.value;
        this.setState({
            destinationSearchText: searchText,
            showDestinationSearchResult: true,
            destinationInvalid: false
        }, async () => {
            if (!searchText.trim()) {
                this.setState({
                    destination: null,
                    destinations: []
                });
            } else {
                this.timeout = setTimeout(async () => {
                    this.setState({loadingDestinationLocations: true});
                    const response = await getAirportCodes(searchText.trim());
                    this.setState({loadingDestinationLocations: false});
                    if (response && response.data) {
                        this.setState({
                            destinations: response.data.map(this.locationMapper),
                            showDestinationSearchResult: true
                        });
                    }
                }, 500);
            }
        });
    };

    public selectOrigin = (origin: any) => {
        this.setState({
            origin,
            loadingOriginLocations: false,
            showOriginSearchResult: false,
            originSearchText: ""
        });
    };

    public selectDestination = (destination: any) => {
        this.setState({
            destination,
            loadingDestinationLocations: false,
            showDestinationSearchResult: false,
            destinationSearchText: ""
        });
    };

    public processError = (error: any) => {
        if (ErrorUtils.requestLimitExceeded(error.response)) {
            this.setState({
                loading: false
            });
            toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't get data."
            });
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (this.state.loading) {
            return;
        }

        if (!this.state.origin || !this.state.destination
            || !this.state.departureDate || !this.state.returnDate) {

            this.setState({
                result: null,
                originInvalid: !this.state.origin,
                destinationInvalid: !this.state.destination,
                departureDateInvalid: !this.state.departureDate,
                returnDateInvalid: !this.state.returnDate
            });

            return;
        }

        this.setState({
            loading: true,
            result: null,
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getTripPurpose({
            departureDate: this.state.departureDate,
            returnDate: this.state.returnDate,
            origin: this.state.origin.code,
            destination: this.state.destination.code,
        }).catch((error) => {
            this.processError(error);
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            const res = response.data;
            this.setState({
                loading: false,
                result: {
                    result: res.result,
                    probability: res.probability
                }
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't get data."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.TRIP_PURPOSE);
    };

    public onReset = (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            departureDate: this.formatDate(this.truncateDate(new Date(new Date().getTime() + this.DAY))),
            departureDateInvalid: false,
            returnDate: this.formatDate(this.truncateDate(new Date(new Date().getTime() + 2 * this.DAY))),
            returnDateInvalid: false,
            loadingOriginLocations: false,
            loadingDestinationLocations: false,
            showOriginSearchResult: false,
            showDestinationSearchResult: false,
            originSearchText: "",
            destinationSearchText: "",
            origins: [],
            destinations: [],
            origin: null,
            destination: null,
            originInvalid: false,
            destinationInvalid: false,
            result: null,
            errorMessage: ""
        });
    };

    public render() {
        return <ModelContentContainer>
            <Card>
                <SectionName>Departure Date:</SectionName>
                <FieldDescription>Should be greater than today.</FieldDescription>
                <Field>
                    <Control>
                        <input
                            style={this.dateInputStyle}
                            type="date"
                            value={this.state.departureDate}
                            onChange={this.handleDepartureDateChange}
                        />
                    </Control>
                    {
                        this.state.departureDateInvalid ? <InputWarning>Please select valid departure date.</InputWarning> : <></>
                    }
                </Field>
                <SectionName>Return Date:</SectionName>
                <FieldDescription>Should be greater than departure date.</FieldDescription>
                <Field>
                    <Control>
                        <input
                            style={this.dateInputStyle}
                            type="date"
                            value={this.state.returnDate}
                            onChange={this.handleReturnDateChange}
                        />
                    </Control>
                    {
                        this.state.returnDateInvalid ? <InputWarning>Please select valid return date.</InputWarning> : <></>
                    }
                </Field>
                <Spacer/>
                <SectionName>Origin:</SectionName>
                {
                    this.state.origin ? <div>
                        {this.state.origin.name}
                    </div> : <div
                        style={this.locationFieldStyle}
                    >Location is not selected.</div>
                }
                {
                    this.state.originInvalid ? <InputWarning>Please select origin.</InputWarning> : <></>
                }
                <LocationContainer>
                    <SmallSection>Search location</SmallSection>
                    <Field>
                        <Control>
                            <Input
                                type="text"
                                placeholder="Search location"
                                value={this.state.originSearchText}
                                onFocus={this.handleOriginFieldFocus}
                                onChange={this.handleOriginSearchTextChange}
                            />
                        </Control>
                    </Field>
                    {
                        this.state.loadingOriginLocations ? <div>
                            <span style={{
                                fontSize: '12px',
                                color: 'grey'
                            }}>Searching locations...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        (this.state.showOriginSearchResult && !this.state.loadingOriginLocations && this.state.origins.length > 0) ?
                            <React.Fragment>
                                <SmallSection>Select Location</SmallSection>
                                <SearchResultContainer>
                                    {
                                        this.state.origins.map((origin: any) => {
                                            return <SearchResult
                                                key={origin.code}
                                                onClick={() => {
                                                    this.selectOrigin(origin);
                                                }}
                                            >
                                                {origin.name}
                                            </SearchResult>;
                                        })
                                    }
                                </SearchResultContainer>
                            </React.Fragment> : <></>
                    }
                </LocationContainer>
                <Spacer/>
                <SectionName>Destination:</SectionName>
                {
                    this.state.destination ? <div>
                        {this.state.destination.name}
                    </div> : <div
                        style={this.locationFieldStyle}
                    >Location is not selected.</div>
                }
                {
                    this.state.destinationInvalid ? <InputWarning>Please select destination.</InputWarning> : <></>
                }
                <LocationContainer>
                    <SmallSection>Search location</SmallSection>
                    <Field>
                        <Control>
                            <Input
                                type="text"
                                placeholder="Search location"
                                value={this.state.destinationSearchText}
                                onFocus={this.handleDestinationFieldFocus}
                                onChange={this.handleDestinationSearchTextChange}
                            />
                        </Control>
                    </Field>
                    {
                        this.state.loadingDestinationLocations ? <div>
                            <span style={{
                                fontSize: '12px',
                                color: 'grey'
                            }}>Searching locations...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        (this.state.showDestinationSearchResult && !this.state.loadingDestinationLocations && this.state.destinations.length > 0) ?
                            <React.Fragment>
                                <SmallSection>Select Location</SmallSection>
                                <SearchResultContainer>
                                    {
                                        this.state.destinations.map((destination: any) => {
                                            return <SearchResult
                                                key={destination.code}
                                                onClick={() => {
                                                    this.selectDestination(destination);
                                                }}
                                            >
                                                {destination.name}
                                            </SearchResult>;
                                        })
                                    }
                                </SearchResultContainer>
                            </React.Fragment> : <></>
                    }
                </LocationContainer>
                <Spacer/>
                <div>
                    <Button onClick={this.onSubmit}>Submit</Button>
                    <Button onClick={this.onReset} style={{marginLeft: "5px"}}>Reset</Button>
                </div>
                {
                    this.state.loading && <div style={{textAlign: 'center'}}>
                        <span>Loading...</span>
                        <Icon className="fas fa-spinner fa-spin"/>
                    </div>
                }
                {
                    !this.state.loading && this.state.errorMessage ? <div>
                        <InformationCard>{this.state.errorMessage}</InformationCard>
                    </div> : <></>
                }
                {
                    !this.state.loading && this.state.result ? <div>
                        <Spacer/>
                        <SectionName>Result:</SectionName>
                        <InformationCard>
                            <p style={{marginLeft: '10px'}}>
                                <span title="Result"><b>{this.state.result.result}</b></span>
                                {" - "}
                                <span title="Probability">{this.state.result.probability}</span>
                            </p>
                        </InformationCard>
                    </div> : <></>
                }
                <Spacer/>
            </Card>
        </ModelContentContainer>;
    }
}

export default TripPurposeModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/TripPurpose/TripPurposeModel.tsx